import { Component, Input, Output, EventEmitter, ViewChild, ContentChild, signal, OnInit, AfterViewInit, forwardRef, DestroyRef, inject, ViewEncapsulation, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { AutoCompleteComponent, AutoCompleteModule } from '@progress/kendo-angular-dropdowns';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { TicomixSearchOptions, TicomixSearchValue, BlurEvent, SearchField } from './ticomix-search.models';
import { TicomixGridWindowComponent } from './ticomix-grid-window.component';
import { IMvcDataSource } from '../kendo-data-source-mvc';
import { IWebApiDataSourceRequest, IDataSourceResult } from '@ticomix/common';
import { SelectionEvent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * A flexible search component that combines autocomplete functionality with advanced search options.
 * 
 * The TicomixSearchComponent provides a searchable dropdown with autocomplete features and 
 * an optional grid-based search window for more complex lookups. It implements ControlValueAccessor
 * for seamless integration with Angular forms.
 * 
 * @example
 * Basic usage with primitive value binding:
 * ```html
 * <ticomix-search 
 *   [options]="searchOptions"
 *   [(ngModel)]="selectedValue"
 *   (valueChange)="onValueChange($event)">
 * </ticomix-search>
 * ```
 * 
 * @example
 * Usage with object binding:
 * ```html
 * <ticomix-search 
 *   [options]="searchOptions"
 *   [useObjectBinding]="true"
 *   [(ngModel)]="selectedObject"
 *   (valueChange)="onValueChange($event)">
 * </ticomix-search>
 * ```
 * 
 * @example
 * Complete example with all event handlers:
 * ```html
 * <ticomix-search 
 *   [options]="searchOptions"
 *   [(ngModel)]="selectedValue"
 *   (valueChange)="onValueChange($event)"
 *   (blur)="onBlur($event)"
 *   (keyPress)="onKeyPress($event)"
 *   (searchButtonClick)="onSearchButtonClick()"
 *   (infoButtonClick)="onInfoClick($event)"
 *   (gotoButtonClick)="onGotoClick($event)">
 * </ticomix-search>
 * ```
 */
@Component({
  selector: 'ticomix-search',
  templateUrl: './ticomix-search.component.html',
  styleUrls: ['./ticomix-search.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None, // Replace ::ng-deep with ViewEncapsulation.None
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    ButtonModule,
    TicomixGridWindowComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TicomixSearchComponent),
      multi: true
    }
  ]
})
export class TicomixSearchComponent implements OnInit, ControlValueAccessor {
  /** Configuration options for the search component */
  @Input() options!: TicomixSearchOptions;

  /**
   * Determines whether the component binds to objects or primitive values.
   * When true, the component will emit and receive full objects.
   * When false, the component will emit and receive string/primitive values.
   * @default false
   */
  @Input() useObjectBinding = false;

  /**
   * Controls whether the search input is disabled.
   * When true, the component will be non-interactive.
   * @default false
   */
  @Input() disabled = false;

  /**
   * Controls whether the search input is readonly.
   * When true, the user can't type but can still interact with buttons.
   * @default false
   */
  @Input() readonly = false;

  // Note: We don't need @Input ngModel or @Output ngModelChange
  // because we're implementing ControlValueAccessor which handles two-way binding automatically

  /**
   * Emits when the value changes with both the string value and the full item object.
   * This provides more context than the standard ngModelChange event.
   */
  @Output() valueChange = new EventEmitter<TicomixSearchValue>();

  /**
   * Emits when the component loses focus, indicating if a search was triggered.
   */
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur = new EventEmitter<BlurEvent>();

  /**
   * Emits when a key is pressed in the input field.
   */
  @Output() keyPress = new EventEmitter<KeyboardEvent>();

  /**
   * Emits when the search button is clicked.
   */
  @Output() searchButtonClick = new EventEmitter<void>();

  /**
   * Emits when the goto button is clicked, providing the current value object.
   */
  @Output() gotoButtonClick = new EventEmitter<TicomixSearchValue>();

  /**
   * Emits when the info button is clicked, providing the current value object.
   */
  @Output() infoButtonClick = new EventEmitter<TicomixSearchValue>();

  /**
   * Emits when an error occurs during data fetching.
   */
  @Output() searchError = new EventEmitter<{ error: any; message: string }>();

  /** Reference to the default grid window component instance */
  @ViewChild('defaultGridWindow') defaultGridWindow?: TicomixGridWindowComponent;

  /** Reference to a custom grid window component provided via content projection */
  @ContentChild(TicomixGridWindowComponent) customGridWindow?: TicomixGridWindowComponent;

  /** Reference to the autocomplete component instance */
  @ViewChild('autoComplete') autoComplete?: AutoCompleteComponent;

  searchControl = new FormControl('');
  filteredItems = signal<any[]>([]);
  loading = signal(false);
  errorMessage = signal<string>('');
  
  // Unique component ID for accessibility
  componentId = `ticomix-search-${Math.random().toString(36).substring(2, 11)}`;

  // Store selected item as a signal for better reactivity
  private _selectedItem = signal<any>(null);

  // Subject for debouncing filter changes
  private filterSubject = new Subject<string>();
  
  // Inject DestroyRef for proper cleanup
  private destroyRef = inject(DestroyRef);

  get selectedItem() {
    return this._selectedItem;
  }

  setSelectedItem(item: any) {
    this._selectedItem.set(item);
  }

  // ControlValueAccessor implementation
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: any) => void = () => { };
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => { };
  private innerValue: any = null;
  private focusedValue: string | null = null;

  ngOnInit(): void {
    if (!this.options) {
      throw new Error('TicomixSearchComponent requires options input');
    }

    // Initialize search control with value if provided
    this.updateDisplayValue(this.innerValue);

    // Set up debounced filter subscription
    const debounceMs = this.options.filterDebounceTime ?? 300; // Use configured time or default to 300ms
    this.filterSubject.pipe(
      debounceTime(debounceMs), // Wait after user stops typing
      distinctUntilChanged(), // Only emit if value is different from previous
      takeUntilDestroyed(this.destroyRef) // Automatically unsubscribe on component destroy
    ).subscribe(text => {
      this.fetchFilteredItems(text);
    });

    // If we have a primitive value and dataValueField is set, try to load the display value
    if (!this.useObjectBinding && this.innerValue !== null && this.innerValue !== undefined && 
        this.options.dataValueField && this.options.dataSource) {
      this.loadDisplayValueForPrimitive(this.innerValue);
    }
  }

  // ControlValueAccessor interface implementation
  writeValue(value: any): void {
    // Update internal value
    this.innerValue = value;

    // If value is being set programmatically, we may need to clear or update selectedItem
    const currentSelectedItem = this.selectedItem();
    if (!value) {
      // If clearing the value, clear the selected item
      this.setSelectedItem(null);
      this.updateDisplayValue(value);
    } else if (this.useObjectBinding) {
      // For object binding, just update display
      this.updateDisplayValue(value);
    } else if (this.options.dataValueField && this.options.dataSource) {
      // For primitive binding with dataValueField, we need to load the item
      // Check if current selected item matches the value
      if (currentSelectedItem && currentSelectedItem[this.options.dataValueField] === value) {
        // Already have the right item selected
        this.updateDisplayValue(currentSelectedItem[this.options.dataTextField]);
      } else {
        // Need to load the item for this value
        this.setSelectedItem(null); // Clear current selection
        this.loadDisplayValueForPrimitive(value);
      }
    } else {
      // No dataValueField, just show the value
      this.updateDisplayValue(value);
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }

  /**
   * Updates the display value in the search control based on the inner value.
   * This handles both primitive values and object values based on the binding type.
   * @param value The value to display in the search control
   */
  private updateDisplayValue(value: any): void {
    if (!value) {
      this.searchControl.setValue('');
      return;
    }

    if (this.useObjectBinding) {
      // Handle object value with dataTextField
      if (value[this.options.dataTextField]) {
        this.searchControl.setValue(value[this.options.dataTextField]);
      } else if (typeof value === 'object') {
        // We have an object but no dataTextField match - look for item property
        if (value.item && value.item[this.options.dataTextField]) {
          this.searchControl.setValue(value.item[this.options.dataTextField]);
        } else {
          this.searchControl.setValue('');
        }
      }
    } else {
      // Handle primitive value (string/number)
      if (typeof value === 'string' || typeof value === 'number') {
        this.searchControl.setValue(value.toString());
      } else if (value.value !== undefined) {
        // Handle case where value is a TicomixSearchValue
        this.searchControl.setValue(value.value);
      } else {
        this.searchControl.setValue('');
      }
    }
  }

  /**
   * Called when the filter text changes in the autocomplete.
   * Fetches filtered items from the data source based on the input text.
   * @param text The filter text entered by the user
   */
  onFilterChange(text: string): void {
    // Push to subject for debouncing instead of calling directly
    this.filterSubject.next(text);
  }

  /**
   * Called when the value changes in the input field.
   * Updates the model value and emits valueChange events.
   * @param value The new value from the input field
   */
  onValueChange(value: string): void {
    let searchValue = value || '';

    // Check if we already have a selected item for this value
    const currentSelectedItem = this.selectedItem();
    if (currentSelectedItem && currentSelectedItem[this.options.dataTextField] === searchValue) {
      // Build the value object with existing selected item
      const valueObj = { value: searchValue, item: currentSelectedItem };

      // Update the model based on binding type
      if (this.useObjectBinding) {
        this.updateModelValue(currentSelectedItem);
      } else {
        // For primitive binding, use the value field if defined, otherwise use display text
        const valueField = this.options.dataValueField;
        const primitiveValue = valueField && currentSelectedItem[valueField] !== undefined 
          ? currentSelectedItem[valueField] : searchValue;
        this.updateModelValue(primitiveValue);
      }

      // Emit the valueChange event with full object
      this.valueChange.emit(valueObj);
      return;
    }

    // Otherwise, search in filtered items
    let selectedItem = this.filteredItems().find(item =>
      item[this.options.dataTextField] === searchValue);

    // Store the selected item for later use
    this.setSelectedItem(selectedItem);

    // Build the value object - ensure item is null for empty values
    const valueObj = { value: searchValue, item: selectedItem || null };

    // Update the model based on binding type
    if (this.useObjectBinding) {
      // For object binding, use the selected item or null if no value
      if (selectedItem) {
        this.updateModelValue(selectedItem);
      } else if (searchValue) {
        // Only create an object if there's actually a search value
        this.updateModelValue({ [this.options.dataTextField]: searchValue });
      } else {
        // If no item and no value, use null
        this.updateModelValue(null);
      }
    } else {
      // For primitive binding, use the value field if item exists, otherwise use search value
      if (selectedItem && this.options.dataValueField) {
        const primitiveValue = selectedItem[this.options.dataValueField];
        this.updateModelValue(primitiveValue !== undefined ? primitiveValue : searchValue);
      } else {
        // If no item and no value, use null for primitive binding too
        this.updateModelValue(searchValue || null);
      }
    }

    // Emit the valueChange event with full object
    this.valueChange.emit(valueObj);
  }

  onFocus(): void {
    // Store the current value when the input field gains focus
    this.focusedValue = this.searchControl.value || '';
  }

  /**
   * Called when the input field loses focus.
   * If the value doesn't match an existing item, it may trigger a search window.
   * Emits a blur event with information about the current state.
   */
  onBlur(): void {
    if (!this.hasValueChanged()) {
      return;
    }

    this.onTouched();

    const searchValue = this.searchControl.value || '';
    const blurResult = this.handleBlurValidation(searchValue);

    // Emit the blur event with validation info
    this.blur.emit(blurResult);
  }

  /**
   * Checks if the value has changed since focus.
   * @returns true if the value has changed, false otherwise
   */
  private hasValueChanged(): boolean {
    return this.focusedValue !== this.searchControl.value;
  }

  /**
   * Handles blur validation logic and returns the blur event data.
   * @param searchValue The current search value
   * @returns BlurEvent data with validation results
   */
  private handleBlurValidation(searchValue: string): BlurEvent {
    if (!searchValue) {
      return { value: searchValue, item: null, searchTriggered: false };
    }

    // Try to find exact match
    const exactMatch = this.findExactMatch(searchValue);
    if (exactMatch) {
      this.setSelectedItem(exactMatch);
      return { value: searchValue, item: exactMatch, searchTriggered: false };
    }

    // Handle no exact match scenarios
    return this.handleNoExactMatch(searchValue);
  }

  /**
   * Finds an exact match for the search value in filtered items.
   * @param searchValue The value to search for
   * @returns The matching item or null
   */
  private findExactMatch(searchValue: string): any {
    return this.filteredItems().find(item =>
      item[this.options.dataTextField] === searchValue);
  }

  /**
   * Handles scenarios when no exact match is found.
   * @param searchValue The current search value
   * @returns BlurEvent data with appropriate state
   */
  private handleNoExactMatch(searchValue: string): BlurEvent {
    const filteredItemsArray = this.filteredItems();

    // If there's only one item in filtered results, auto-select it
    if (filteredItemsArray.length === 1) {
      const singleItem = filteredItemsArray[0];
      this.updateWithSelectedItem(singleItem);
      return { value: searchValue, item: singleItem, searchTriggered: false };
    }

    // Otherwise, open search window if available
    const searchTriggered = this.openSearchWindow(searchValue);
    return { value: searchValue, item: null, searchTriggered };
  }

  /**
   * Opens the search window if available.
   * @param searchValue The value to search with
   * @returns true if search window was opened, false otherwise
   */
  private openSearchWindow(searchValue: string): boolean {
    const gridWindow = this.customGridWindow || this.defaultGridWindow;
    
    if (gridWindow) {
      gridWindow.open(searchValue);
      return true;
    }
    
    return false;
  }

  /**
   * Called when an item is selected from the grid window.
   * Updates the component with the selected item data.
   * @param event The selection event from the Kendo grid
   */
  onRowSelect(event: SelectionEvent): void {
    if (!event.selectedRows || event.selectedRows.length === 0) return;
    const selectedItem = event.selectedRows[0].dataItem;

    if (!selectedItem) return;

    // Update the value
    this.updateWithSelectedItem(selectedItem);
  }

  /**
   * Called when a key is pressed in the input field.
   * Special handling for the Enter key to process value changes.
   * @param event The keyboard event
   */
  onKeyPress(event: KeyboardEvent): void {
    // Handle enter key specifically
    if (event.key === 'Enter') {
      event.preventDefault();

      // Get the current value
      let searchValue = this.searchControl.value || '';

      // Process value change
      this.onValueChange(searchValue);

      // Emit the keypress event for additional handling
      this.keyPress.emit(event);
    }
  }

  /**
   * Called when the search button is clicked.
   * Opens the grid window search dialog with optional initial search value.
   */
  onSearchClick(): void {
    // Get the grid window (either custom or default)
    const gridWindow = this.customGridWindow || this.defaultGridWindow;

    if (gridWindow) {
      // Set initial search value if configured
      if (this.options.autoFillSearch) {
        const searchValue = this.searchControl.value || '';
        gridWindow.open(searchValue);
      } else {
        gridWindow.open();
      }

      // Emit the searchButtonClick event for additional handling
      this.searchButtonClick.emit();
    }
  }

  /**
   * Called when the info button is clicked.
   * Calls the configured info button handler and emits an event.
   */
  onInfoClick(): void {
    const valueObject = this.getCurrentValueObject();
    this.options.buttons?.infoButtonHandler?.(valueObject);
    this.infoButtonClick.emit(valueObject);
  }

  /**
   * Called when the goto button is clicked.
   * Calls the configured goto button handler and emits an event.
   */
  onGotoClick(): void {
    const valueObject = this.getCurrentValueObject();
    this.options.buttons?.gotoButtonHandler?.(valueObject);
    this.gotoButtonClick.emit(valueObject);
  }

  /**
   * Updates component value when an item is selected.
   * Handles both object and primitive binding modes.
   * @param item The selected item data
   */
  private updateWithSelectedItem(item: any): void {
    if (!item) return;

    // Store the selected item
    this.setSelectedItem(item);

    // Update the display text in the input field
    const displayText = item[this.options.dataTextField] || '';
    this.searchControl.setValue(displayText);

    // Create a value object for event emissions
    const valueObj: TicomixSearchValue = { value: displayText, item: item };

    // Update the model based on binding type
    if (this.useObjectBinding) {
      this.updateModelValue(item);
    } else {
      // For primitive binding, use the value field if defined, otherwise use display text
      const valueField = this.options.dataValueField;
      const primitiveValue = valueField && item[valueField] !== undefined ? item[valueField] : displayText;
      this.updateModelValue(primitiveValue);
    }

    // Emit the valueChange event with full object
    this.valueChange.emit(valueObj);
  }

  /**
   * Updates the model value and triggers change events.
   * Core method for the ControlValueAccessor implementation.
   * @param value The new value to set
   */
  private updateModelValue(value: any): void {
    this.innerValue = value;
    this.onChange(value);
  }

  /**
   * Gets the current value object for button handlers.
   * Constructs a TicomixSearchValue with the current state.
   * @returns A TicomixSearchValue object with the current value and item
   */
  private getCurrentValueObject(): TicomixSearchValue {
    const searchValue = this.searchControl.value || '';
    const currentSelectedItem = this.selectedItem();

    // If the search value is empty, return null item
    if (!searchValue) {
      return { value: searchValue, item: null };
    }

    // First try to use the stored selected item if the value matches
    if (currentSelectedItem && currentSelectedItem[this.options.dataTextField] === searchValue) {
      return { value: searchValue, item: currentSelectedItem };
    }

    // Otherwise, try to find it in filtered items
    const selectedItem = this.filteredItems().find(item =>
      item[this.options.dataTextField] === searchValue);

    // For object binding, only use innerValue if it's not an empty object
    let finalItem = selectedItem;
    if (!finalItem && this.useObjectBinding && this.innerValue) {
      // Check if innerValue is an empty object or has meaningful data
      const hasData = this.innerValue[this.options.dataTextField];
      finalItem = hasData ? this.innerValue : null;
    }

    return { value: searchValue, item: finalItem || null };
  }

  /**
   * Fetches filtered items from the datasource based on input text.
   * Creates a filter query and sends it to the data source.
   * @param text The text to use for filtering items
   */
  private fetchFilteredItems(text: string): void {
    if (!text || !this.options.dataSource) {
      this.filteredItems.set([]);
      return;
    }

    this.loading.set(true);

    // Create a filter string for the request
    const filterObj: CompositeFilterDescriptor = {
      logic: 'or',
      filters: [
        {
          field: this.options.dataTextField,
          operator: 'contains',
          value: text
        }
      ]
    };

    // Create a request using the MVC DataSource format
    const request: IWebApiDataSourceRequest = {
      filter: JSON.stringify(filterObj),
      sort: null,
      group: null,
      page: 1,
      pageSize: 10,
      skip: 0,
      take: 10,
      aggregate: null,
      mode: null,
      groupPaging: null,
      includeSubGroupCount: null
    };

    let state: State = {
      filter: filterObj,
      group: undefined,
      skip: 0,
      take: 10,
      sort: undefined
    };

    // Call the data source with the request
    this.options.dataSource.action(request, state).pipe(
      takeUntilDestroyed(this.destroyRef) // Automatically unsubscribe on component destroy
    ).subscribe({
      next: (result: IDataSourceResult<any>) => {
        // Update the filtered items with the results
        this.filteredItems.set(result.Data || []);
        this.loading.set(false);
        this.errorMessage.set(''); // Clear any previous errors
      },
      error: (error) => {
        const errorMsg = 'Failed to load search results. Please try again.';
        this.filteredItems.set([]);
        this.loading.set(false);
        this.errorMessage.set(errorMsg);
        
        // Emit error event for custom handling
        this.searchError.emit({ error, message: errorMsg });
        
        // Clear error message after 5 seconds
        setTimeout(() => {
          this.errorMessage.set('');
        }, 5000);
      }
    });
  }

  get showSearchButton(): boolean {
    let value = this.options.buttons?.showSearchButton;
    if (value === undefined) {
      value = true;
    }
    return value;
  }

  get showInfoButton(): boolean {
    return this.options.buttons?.showInfoButton || false;
  }

  get showGotoButton(): boolean {
    return this.options.buttons?.showGotoButton || false;
  }

  /**
   * Clears the search field and resets the value.
   * Useful for programmatically resetting the component state.
   */
  clear(): void {
    // Clear the search control
    this.searchControl.setValue('');

    // Clear filtered items
    this.filteredItems.set([]);

    // Clear selected item
    this.setSelectedItem(null);

    // Update model value - use null for both binding types when clearing
    this.updateModelValue(null);

    // Emit valueChange event
    this.valueChange.emit({ value: '', item: null });
  }

  /**
   * Programmatically focuses the search field.
   * Useful for setting initial focus on the component.
   */
  focus(): void {
    this.focusedValue = this.searchControl.value || '';
    // Use Kendo's built-in focus method on the AutoComplete component
    if (this.autoComplete) {
      this.autoComplete.focus();
    }
  }

  /**
   * Handles keyboard shortcuts for accessibility.
   * Alt+S: Open search window
   * Alt+I: View info (if info button is shown)
   * Alt+G: Go to item (if goto button is shown)
   * @param event The keyboard event
   */
  @HostListener('keydown', ['$event'])
  handleKeyboardShortcuts(event: KeyboardEvent): void {
    // Skip if component is disabled
    if (this.disabled) {
      return;
    }
    
    // Check if Alt key is pressed
    if (!event.altKey) {
      return;
    }

    switch(event.key.toLowerCase()) {
      case 's':
        // Alt+S: Open search window
        if (this.showSearchButton) {
          event.preventDefault();
          this.onSearchClick();
        }
        break;
      
      case 'i':
        // Alt+I: View info
        if (this.showInfoButton && this.selectedItem()) {
          event.preventDefault();
          this.onInfoClick();
        }
        break;
      
      case 'g':
        // Alt+G: Go to item
        if (this.showGotoButton && this.selectedItem()) {
          event.preventDefault();
          this.onGotoClick();
        }
        break;
    }
  }

  /**
   * Loads the display value for a primitive value by fetching from the data source.
   * This is used when we have a primitive ID value and need to display the corresponding text.
   * @param primitiveValue The primitive value (e.g., ID) to load display text for
   */
  private loadDisplayValueForPrimitive(primitiveValue: any): void {
    if (!this.options.dataSource || !this.options.dataValueField) {
      // Just display the primitive value if we can't load the item
      this.searchControl.setValue(primitiveValue.toString());
      return;
    }

    // Create a filter to find the item by value field
    const filterObj: CompositeFilterDescriptor = {
      logic: 'and',
      filters: [
        {
          field: this.options.dataValueField,
          operator: 'eq',
          value: primitiveValue
        }
      ]
    };

    // Create a request to fetch the specific item
    const request: IWebApiDataSourceRequest = {
      filter: JSON.stringify(filterObj),
      sort: null,
      group: null,
      page: 1,
      pageSize: 1,
      skip: 0,
      take: 1,
      aggregate: null,
      mode: null,
      groupPaging: null,
      includeSubGroupCount: null
    };

    const state: State = {
      filter: filterObj,
      group: undefined,
      skip: 0,
      take: 1,
      sort: undefined
    };

    // Fetch the item from the data source
    this.options.dataSource.action(request, state).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (result: IDataSourceResult<any>) => {
        if (result.Data && result.Data.length > 0) {
          const item = result.Data[0];
          this.setSelectedItem(item);
          // Update display with the text field value
          this.searchControl.setValue(item[this.options.dataTextField] || '');
        } else {
          // Item not found, just display the primitive value
          this.searchControl.setValue(primitiveValue.toString());
        }
      },
      error: (error) => {
        // On error, just display the primitive value
        console.error('Failed to load display value for primitive:', error);
        this.searchControl.setValue(primitiveValue.toString());
      }
    });
  }
}